<template>
  <div>
    <select-field ref="AdresseSummary"
                  :value=display
                  :required=required
                  :editFieldInfo=editFieldInfo
                  :items=items
                  :disabled=readOnly
                  :canEdit=canEdit
                  :selectText=selectText
                  :hideLabel='hideLabel'
                  :dataTable='dataTable'
                  :showMissing=showMissing
                  :notOrdered=notOrdered
                  :fiche=fiche
                  @updated=setUpdated
                  @onEdit=onEdit
                  @error=setError>
    </select-field> 

    <section-container-panel v-if="dialogOpen"
                             :section="section"
                             :dialogOpen="dialogOpen"
                             :extended="true"
                             :helper="helper"
                             :ressourceHeader="ressourceHeader"
                             :fiche="fiche"
                             @updated="setSectionUpdated"
                             @closeDialog="closeDialog"
                             @saveOrCancel="setSaveOrCancel"
                             @status="statusUpdated">
    </section-container-panel>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import Section from '@/framework/base/classes/Section';
  import { FieldInfo, EditFieldType, FieldStatus } from '@/framework/base/components/FieldInfo'
  import SummaryBase from '@/framework/base/components/SummaryBase';
  import { Sections } from '@/generated/classes/AppSchema';
  import SelectField from '@/framework/base/components/SelectField.vue'
  import Item from '@/framework/base/classes/Item';
  import Adresse from '@/extend/classes/Adresse';
  import Owner from '@/extend/classes/Owner';
  import SectionContainerPanel from '@/framework/base/components/SectionContainerPanel.vue';
  import SectionAdresseExtend from '@/extend/sections/SectionAdresseExtend.vue';
  import { storeAdresse } from '@/generated/stores';
  import ListData, { ListFilter } from '@/extend/classes/ListData';

  export default defineComponent({
    extends: SummaryBase,
    components: {
      SelectField,
      SectionContainerPanel,
    },

    props: {
      helper: {
        type: Boolean
      }
    },

    beforeUnmount (): void {
      this.clearStore();
    },

    data() {
      const listAdresse: Adresse[] = [];
      const tmpAdresse: Adresse = new Adresse();
      return {
        selectedAdresseId: 0,
        selectedAdresse: new Adresse(),
        tmpAdresse,
        newContext: true,
        reset: true,
        listUpdated: false,
        listAdresse
      }
    },

    computed: {
      editFieldInfo (): FieldInfo {
        const type = EditFieldType.SUMMARY;
        const precision = 2;
        const format = null;
        const mask = null;
        const focus = null;
        const live = null;
        return new FieldInfo(this.ressource, type, precision, mask, format, focus, live, this.theme);
      },

      section (): Section {
        const section = new Section();
        section.component = SectionAdresseExtend;
        section.type = 'adresse';
        section.data = this.selectedAdresse;
        section.picto = 'SectionAdresseExtendPicto';
        section.contextId = this.contextId;
        section.showButton = true;
        section.autoSave = true;
        if (this.sectionName) {
          section.sectionName = this.sectionName;
        }
        if (this.requiredAttributs) {
          section.requiredAttributs = this.requiredAttributs;
        }
        return section;
      },

      listItems (): Adresse[] {
        if (this.reset != this.toggleReset) {
          this.loadItems();
        }
        return this.listAdresse;
      },

      items (): Item[] {
        const items: Item[] = [];
        items.push(new Item(0, this.newLabel != undefined && this.newLabel != '' ? this.newLabel : "Nouvelle adresse")); 
        const adresses = this.getItems(this.listItems, this.sectionName);
        adresses.forEach(adresse => {
          if (adresse && adresse.id > 0 ) {
            items.push(new Item(adresse.id, this.getDisplay(adresse)));
          }
        })
        return items;
      },

      display (): string {
        const value = this.value;
        let item = this.items.find(a => a.key == value)
        this.changeContext();
        if (this.items.length == 2 && this.newContext && this.required && this.autoSelect) {
          this.changeContext();
          item = this.items.find(a => a.key == this.items[1].key);
          if (item && item.key != this.initValue) {
            this.itemChanged(item.key);
            this.setStatusUpdated(true);
            if (this.initValue != 0 || this.items.length != 2) {
              this.statusUpdated(this.status);
            }
          }
        }
        if (item) {
          if (this.resetToggle()) {
            this.setStatusUpdated(true);
            this.statusUpdated(this.status);
          }
          const itemDefined = item;
          const adresse = this.listAdresse.find(a => a.id == itemDefined.key);
          if (adresse && adresse.id != this.selectedAdresseId) {
            this.setSelectedAdresse(adresse);
          }
          if (item.key == 0 && this.selectedAdresseId == 0) {
            this.setSelectedAdresse(this.newAdresse());
          }
          this.setSelectedAdresseId(adresse ? adresse.id : this.selectedAdresse.id);
        }
        const res = item && item.key != 0 ? item.title : '';
        if (this.cancelChanges && item) {
          this.itemChanged(item.key);
        }
        this.setAttribut(this.selectedAdresse, this.owner, this.parentObj)
        return res
      },

      canEdit (): string {
        return true
      }
    },

    methods: {
      setSelectedAdresse(value: Adresse) {
        this.selectedAdresse = value;
      },
      setSelectedAdresseId(id: number) {
        this.selectedAdresseId = id;
      },
      setNewContext(value: boolean) {
        this.newContext = value;
      },
      setStatusUpdated(value: boolean) {
        this.status.updated = value;
      },
      newAdresse (): Adresse {
        this.clearStore();
        const newAdresse = storeAdresse.createNewAdresse();
        this.tmpAdresse = newAdresse;
        return newAdresse;
      },

      loadItems (): Promise<boolean> {
        this.reset = this.toggleReset;
        return new Promise((resolve) => {
          if (this.filter) {
            Adresse.list(this.filter)
              .then((data: ListData) => {
                const items = data.reccords as Adresse[];
                this.listAdresse = [];
                items.forEach((item) => {
                  this.listAdresse.push(Adresse.fromJson(item));
                })
                this.listUpdated = true;
                resolve(true)
              }).catch(() => {
                //Catch empty list
              });
          } else {
            resolve(false);
          }
        })
      },

      clearStore() : void {
        if (this.tmpAdresse != null && this.tmpAdresse.id < 0) {
          storeAdresse.mAdresseDeleted(this.tmpAdresse.id);
        }
      },

      contextChanged() : void {
        this.newContext = true;
        this.selectedAdresseId = 0;
        this.selectedAdresse = new Adresse();
        this.initValue = this.value;
      },

      setSaveOrCancel () {
        this.cancelChanges = true;
      },

      // eslint-disable-next-line
      getItems(adresse: Adresse[], section: String): Adresse[] {
        const items: Adresse[] = [];
        adresse.forEach((item) => {
          items.push(Adresse.fromJson(item));
        })
        return items;
      },

        // eslint-disable-next-line
      getDisplay(adresse: Adresse): string {
        return '';
      },

      // eslint-disable-next-line
      setAttribut(adresse: Adresse, owner: Owner, parentObj: unknown): void {
        // to implement
      },

      setSectionUpdated(adresse: Adresse) {
        const item = this.items.find(a => a.key == adresse.id);
        if (!item && adresse.id > 0) {
          this.loadItems().then(() => {
            const item = this.items.find(a => a.key == adresse.id);
            if (item) {
              this.setUpdated(item);
            }
          });
        }
        if (item) {
          this.setUpdated(item);
        }
      },

      setUpdated (item: Item): void {
        if (item.key == 0) {
          this.dialogOpen = true;
          this.selectedAdresse = this.newAdresse();
        }
        this.itemChanged(item.key);
        this.status.updated = this.initValue != item.key;
        this.status.error = false;
        this.statusUpdated(this.status);
      },

      setRestored (adresse: Adresse) {
        this.selectedAdresse = adresse;
      },

      itemChanged (id: number): void {
        this.$emit('update:value', id);
      },

      statusUpdated (status: FieldStatus): void {
        this.$emit('status', status);
      },

      missing (): boolean {
        // @ts-expect-error : generated method
        return this.$refs.AdresseSummary.missing();
      },

      touch (): boolean {
        // @ts-expect-error : generated method
        return this.$refs.AdresseSummary.touch();
      }
    },
  })
</script>
